import {FC, useEffect, useMemo, useState} from 'react'
import * as Yup from 'yup'
import {FieldArray, FormikProvider, useFormik} from 'formik'
import clsx from 'clsx'
import {useIntl} from 'react-intl'
import {useNavigate, useParams} from 'react-router-dom'
import {toast} from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import {AzPageHeader} from '../../components/AzPageHeader'
import StickyLanguageSelector from '../../components/StickyLanguageSelector'
import {DataLoading} from '../../../_dashboard/partials/components/loading/DataLoading'
import {createMailOrder, getMailOrderDetails, updateMailOrder} from '../../services/mailOrders'

const CURRENCIES = ['TND', 'EUR', 'USD']
const CLIENT_LANGUAGES = ['fr', 'en', 'de'] as const
const AMOUNT_TOLERANCE = 0.001

type InstallmentFormValue = {
  amount: string | number
  expiresAt: string
}

type MailOrderFormValues = {
  circuitName: string
  circuitStartDate: string
  circuitEndDate: string
  bookingReference: string
  participantsCount: string | number
  description: string
  circuitSummary: string
  currency: string
  totalPrice: string | number
  clientName: string
  clientEmail: string
  clientPhone: string
  clientLanguage: string
  clientCountry: string
  withVoucher: boolean
  installments: InstallmentFormValue[]
}

const toInputDate = (value?: string | Date): string => {
  if (!value) return ''
  const d = new Date(value)
  if (isNaN(d.getTime())) return ''
  return d.toISOString().slice(0, 10)
}

const todayInputDate = (): string => toInputDate(new Date())

const moneyFactor = (currency: string): number => (currency === 'TND' ? 1000 : 100)

const roundAmount = (value: number, currency = 'TND'): number => {
  const factor = moneyFactor(currency)
  return Math.round((value + Number.EPSILON) * factor) / factor
}

const toNumber = (value: string | number | undefined): number => {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

const formatAmount = (amount: number, currency: string) => {
  try {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: currency || 'TND',
      maximumFractionDigits: 3,
    }).format(amount)
  } catch {
    return `${amount} ${currency || ''}`
  }
}

const initialValues: MailOrderFormValues = {
  circuitName: '',
  circuitStartDate: '',
  circuitEndDate: '',
  bookingReference: '',
  participantsCount: 1,
  description: '',
  circuitSummary: '',
  currency: 'TND',
  totalPrice: '',
  clientName: '',
  clientEmail: '',
  clientPhone: '',
  clientLanguage: 'fr',
  clientCountry: '',
  withVoucher: false,
  installments: [{amount: '', expiresAt: ''}],
}

const MailOrderFormPage: FC = () => {
  const intl = useIntl()
  const navigate = useNavigate()
  const {id} = useParams<{id: string}>()
  const isEdit = Boolean(id)
  const [loading, setLoading] = useState(isEdit)
  const [readOnly, setReadOnly] = useState(false)

  const schema = useMemo(
    () =>
      Yup.object().shape({
        circuitName: Yup.string().trim().required(intl.formatMessage({id: 'COMMON.VALIDATION.REQUIRED'})),
        circuitStartDate: Yup.string().required(intl.formatMessage({id: 'COMMON.VALIDATION.REQUIRED'})),
        circuitEndDate: Yup.string().required(intl.formatMessage({id: 'COMMON.VALIDATION.REQUIRED'})),
        participantsCount: Yup.number()
          .typeError(intl.formatMessage({id: 'COMMON.VALIDATION.INVALID_NUMBER'}))
          .min(1)
          .required(intl.formatMessage({id: 'COMMON.VALIDATION.REQUIRED'})),
        currency: Yup.string()
          .matches(/^[A-Z]{3}$/, intl.formatMessage({id: 'MAIL_ORDER.VALIDATION.CURRENCY'}))
          .required(intl.formatMessage({id: 'COMMON.VALIDATION.REQUIRED'})),
        totalPrice: Yup.number()
          .typeError(intl.formatMessage({id: 'COMMON.VALIDATION.INVALID_NUMBER'}))
          .moreThan(0, intl.formatMessage({id: 'MAIL_ORDER.VALIDATION.AMOUNT_POSITIVE'}))
          .required(intl.formatMessage({id: 'COMMON.VALIDATION.REQUIRED'})),
        clientName: Yup.string().trim().required(intl.formatMessage({id: 'COMMON.VALIDATION.REQUIRED'})),
        clientEmail: Yup.string()
          .email(intl.formatMessage({id: 'COMMON.VALIDATION.INVALID_EMAIL'}))
          .required(intl.formatMessage({id: 'COMMON.VALIDATION.REQUIRED'})),
        clientPhone: Yup.string()
          .matches(
            /^\+?[0-9\s().-]{6,20}$/,
            intl.formatMessage({id: 'COMMON.VALIDATION.INVALID_PHONE'})
          )
          .required(intl.formatMessage({id: 'COMMON.VALIDATION.REQUIRED'})),
        clientLanguage: Yup.string().oneOf([...CLIENT_LANGUAGES]).required(),
        installments: Yup.array()
          .of(
            Yup.object().shape({
              amount: Yup.number()
                .typeError(intl.formatMessage({id: 'COMMON.VALIDATION.INVALID_NUMBER'}))
                .moreThan(0, intl.formatMessage({id: 'MAIL_ORDER.VALIDATION.AMOUNT_POSITIVE'}))
                .required(intl.formatMessage({id: 'COMMON.VALIDATION.REQUIRED'})),
              expiresAt: Yup.string().required(intl.formatMessage({id: 'COMMON.VALIDATION.REQUIRED'})),
            })
          )
          .min(1)
          .test(
            'sum-equals-total',
            intl.formatMessage({id: 'MAIL_ORDER.VALIDATION.INSTALLMENTS_SUM'}),
            function (installments) {
              const total = Number(this.parent.totalPrice)
              if (!total || !installments) return true
              const sum = installments.reduce(
                (acc: number, inst: any) => acc + (Number(inst?.amount) || 0),
                0
              )
              return Math.abs(sum - total) <= AMOUNT_TOLERANCE
            }
          ),
      }),
    [intl]
  )

  const formik = useFormik<MailOrderFormValues>({
    initialValues,
    validationSchema: schema,
    enableReinitialize: true,
    onSubmit: async (values, {setSubmitting}) => {
      setSubmitting(true)
      try {
        const payload = {
          circuitName: values.circuitName.trim(),
          circuitStartDate: new Date(values.circuitStartDate).toISOString(),
          circuitEndDate: new Date(values.circuitEndDate).toISOString(),
          bookingReference: values.bookingReference.trim() || undefined,
          participantsCount: Number(values.participantsCount),
          description: values.description.trim() || undefined,
          circuitSummary: values.circuitSummary.trim() || undefined,
          currency: values.currency,
          totalPrice: roundAmount(Number(values.totalPrice), values.currency),
          clientName: values.clientName.trim(),
          clientEmail: values.clientEmail.trim(),
          clientPhone: values.clientPhone.trim(),
          clientLanguage: values.clientLanguage,
          clientCountry: values.clientCountry.trim() || undefined,
          withVoucher: Boolean(values.withVoucher),
          installments: values.installments.map((inst) => ({
            amount: roundAmount(Number(inst.amount), values.currency),
            expiresAt: new Date(inst.expiresAt).toISOString(),
          })),
        }

        const response = isEdit
          ? await updateMailOrder(id as string, payload)
          : await createMailOrder(payload)

        if (response?.payload?.code === 2021) {
          toast.success(
            intl.formatMessage({
              id: isEdit ? 'MAIL_ORDER.UPDATE_SUCCESS' : 'MAIL_ORDER.CREATE_SUCCESS',
            }),
            {position: 'top-right', autoClose: 4000}
          )
          const createdId = response?.data?._id || id
          navigate(`/apps/mail-orders/${createdId}`)
        } else {
          toast.error(intl.formatMessage({id: 'COMMON.ERROR.GENERIC'}), {
            position: 'top-right',
            autoClose: 4000,
          })
        }
      } catch (error: any) {
        const message =
          error?.response?.data?.message || intl.formatMessage({id: 'COMMON.ERROR.GENERIC'})
        toast.error(Array.isArray(message) ? message.join(', ') : message, {
          position: 'top-right',
          autoClose: 5000,
        })
      } finally {
        setSubmitting(false)
      }
    },
  })

  useEffect(() => {
    if (!isEdit) return
    setLoading(true)
    getMailOrderDetails(id as string)
      .then((details: any) => {
        const mailOrder = details?.mailOrder
        if (!mailOrder) return
        if (mailOrder.status === 'paid' || mailOrder.status === 'cancelled') {
          setReadOnly(true)
        }
        void formik.resetForm({
          values: {
            circuitName: mailOrder.circuitName || '',
            circuitStartDate: toInputDate(mailOrder.circuitStartDate),
            circuitEndDate: toInputDate(mailOrder.circuitEndDate),
            bookingReference: mailOrder.bookingReference || '',
            participantsCount: mailOrder.participantsCount ?? 1,
            description: mailOrder.description || '',
            circuitSummary: mailOrder.circuitSummary || '',
            currency: mailOrder.currency || 'TND',
            totalPrice: mailOrder.totalPrice ?? '',
            clientName: mailOrder.clientName || '',
            clientEmail: mailOrder.clientEmail || '',
            clientPhone: mailOrder.clientPhone || '',
            clientLanguage: mailOrder.clientLanguage || 'fr',
            clientCountry: mailOrder.clientCountry || '',
            withVoucher: Boolean(mailOrder.withVoucher),
            installments: (details?.installments || []).map((inst: any) => ({
              amount: inst.amount,
              expiresAt: toInputDate(inst.expiresAt),
            })),
          },
        })
      })
      .catch(() => {
        toast.error(intl.formatMessage({id: 'COMMON.ERROR.GENERIC'}), {position: 'top-right'})
      })
      .finally(() => setLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  const installmentsSum = formik.values.installments.reduce(
    (acc, inst) => acc + toNumber(inst.amount),
    0
  )
  const totalPriceNumber = toNumber(formik.values.totalPrice)
  const remainingToAllocate = roundAmount(totalPriceNumber - installmentsSum, formik.values.currency)
  const amountsMatch =
    totalPriceNumber > 0 && Math.abs(remainingToAllocate) <= AMOUNT_TOLERANCE
  const userChangedInstallments =
    totalPriceNumber > 0 &&
    formik.values.installments.some((inst) => String(inst.amount) !== '') &&
    Math.abs(installmentsSum - totalPriceNumber) > AMOUNT_TOLERANCE

  const handleStartDateChange = (value: string) => {
    formik.setFieldValue('circuitStartDate', value)
    if (!formik.values.circuitEndDate || formik.values.circuitEndDate < value) {
      formik.setFieldValue('circuitEndDate', value)
    }
    if (!formik.values.installments[0]?.expiresAt) {
      formik.setFieldValue('installments.0.expiresAt', value)
    }
  }

  const handleTotalPriceChange = (value: string) => {
    formik.setFieldValue('totalPrice', value)
    if (formik.values.installments.length === 1) {
      formik.setFieldValue('installments.0.amount', value)
    }
  }

  const defaultInstallmentExpiry = () =>
    formik.values.circuitStartDate || todayInputDate()

  const fieldClass = (name: keyof MailOrderFormValues) =>
    clsx('form-control form-control-solid', {
      'is-invalid': formik.touched[name] && formik.errors[name],
    })

  const renderError = (name: keyof MailOrderFormValues) =>
    formik.touched[name] && formik.errors[name] ? (
      <div className='invalid-feedback'>{String(formik.errors[name])}</div>
    ) : null

  const renderSectionTitle = (titleId: string, hintId: string, icon: string) => (
    <div className='d-flex align-items-start gap-3 mb-6'>
      <span className='bullet bullet-vertical h-40px bg-primary mt-1' />
      <div className='symbol symbol-45px symbol-circle bg-light-primary flex-shrink-0'>
        <span className='symbol-label'>
          <i className={`bi ${icon} fs-2 text-primary`} />
        </span>
      </div>
      <div>
        <h2 className='fs-2 fw-bold text-gray-900 mb-1'>{intl.formatMessage({id: titleId})}</h2>
        <p className='text-muted fs-7 mb-0'>{intl.formatMessage({id: hintId})}</p>
      </div>
    </div>
  )

  return (
    <>
      <AzPageHeader
        title={intl.formatMessage({id: isEdit ? 'MAIL_ORDER.EDIT' : 'MAIL_ORDER.ADD'})}
        subtitle={intl.formatMessage({id: 'MAIL_ORDER_PAGE.BREADCRUMB'})}
      />
      <FormikProvider value={formik}>
        <form className='form' onSubmit={formik.handleSubmit} noValidate>
          <fieldset disabled={readOnly || formik.isSubmitting} style={{border: 'none', padding: 0, margin: 0}}>
            {readOnly && (
              <div className='alert alert-warning mb-6'>
                {intl.formatMessage({id: 'MAIL_ORDER.PAID_READONLY'})}
              </div>
            )}

            {/* ── Langue (même UI que création Tour) ─────────────────── */}
            <StickyLanguageSelector
              selectedLanguage={formik.values.clientLanguage}
              onLanguageChange={(lang) => {
                if (readOnly || formik.isSubmitting) return
                formik.setFieldValue('clientLanguage', lang)
              }}
              allowedLanguageCodes={[...CLIENT_LANGUAGES]}
              hint={intl.formatMessage({id: 'MAIL_ORDER.FIELDS.LANGUAGE_HINT'})}
            />

            {/* ── Tour ───────────────────────────────────────────────── */}
            <div className='card border-0 shadow-sm mb-8'>
              <div className='card-body p-6 p-lg-8'>
                {renderSectionTitle('MAIL_ORDER.SECTION.TRIP', 'MAIL_ORDER.SECTION.TRIP_HINT', 'bi-map')}

                <div className='row g-5'>
                  <div className='col-md-6'>
                    <label className='required fw-semibold fs-6 mb-2'>
                      {intl.formatMessage({id: 'MAIL_ORDER.FIELDS.NAME'})}
                    </label>
                    <input
                      type='text'
                      className={fieldClass('circuitName')}
                      {...formik.getFieldProps('circuitName')}
                    />
                    {renderError('circuitName')}
                  </div>
                  <div className='col-md-6'>
                    <label className='fw-semibold fs-6 mb-2'>
                      {intl.formatMessage({id: 'MAIL_ORDER.FIELDS.BOOKING_REFERENCE'})}
                    </label>
                    <input
                      type='text'
                      className='form-control form-control-solid'
                      {...formik.getFieldProps('bookingReference')}
                    />
                  </div>
                  <div className='col-md-3'>
                    <label className='required fw-semibold fs-6 mb-2'>
                      {intl.formatMessage({id: 'MAIL_ORDER.FIELDS.START_DATE'})}
                    </label>
                    <input
                      type='date'
                      className={fieldClass('circuitStartDate')}
                      value={formik.values.circuitStartDate}
                      onChange={(e) => handleStartDateChange(e.target.value)}
                      onBlur={formik.handleBlur}
                      name='circuitStartDate'
                    />
                    {renderError('circuitStartDate')}
                  </div>
                  <div className='col-md-3'>
                    <label className='required fw-semibold fs-6 mb-2'>
                      {intl.formatMessage({id: 'MAIL_ORDER.FIELDS.END_DATE'})}
                    </label>
                    <input
                      type='date'
                      min={formik.values.circuitStartDate || undefined}
                      className={fieldClass('circuitEndDate')}
                      {...formik.getFieldProps('circuitEndDate')}
                    />
                    {renderError('circuitEndDate')}
                  </div>
                  <div className='col-md-3'>
                    <label className='required fw-semibold fs-6 mb-2'>
                      {intl.formatMessage({id: 'MAIL_ORDER.FIELDS.PARTICIPANTS'})}
                    </label>
                    <input
                      type='number'
                      min={1}
                      className={fieldClass('participantsCount')}
                      {...formik.getFieldProps('participantsCount')}
                    />
                    {renderError('participantsCount')}
                  </div>
                  <div className='col-md-3'>
                    <label className='required fw-semibold fs-6 mb-2'>
                      {intl.formatMessage({id: 'MAIL_ORDER.FIELDS.CURRENCY'})}
                    </label>
                    <select
                      className={clsx('form-select form-select-solid', {
                        'is-invalid': formik.touched.currency && formik.errors.currency,
                      })}
                      {...formik.getFieldProps('currency')}
                    >
                      {CURRENCIES.map((currency) => (
                        <option key={currency} value={currency}>
                          {intl.formatMessage({id: `MAIL_ORDER.CURRENCY.${currency}`})}
                        </option>
                      ))}
                    </select>
                    <div className='form-text'>
                      {intl.formatMessage({id: 'MAIL_ORDER.FIELDS.CURRENCY_HINT'})}
                    </div>
                    {renderError('currency')}
                  </div>
                  <div className='col-md-6'>
                    <label className='required fw-semibold fs-6 mb-2'>
                      {intl.formatMessage({id: 'MAIL_ORDER.FIELDS.TOTAL_PRICE'})}
                    </label>
                    <div className='d-flex align-items-stretch gap-3'>
                      <input
                        type='number'
                        min={0}
                        step={formik.values.currency === 'TND' ? '0.001' : '0.01'}
                        className={clsx(fieldClass('totalPrice'), 'flex-grow-1')}
                        name='totalPrice'
                        value={formik.values.totalPrice}
                        onBlur={formik.handleBlur}
                        onChange={(e) => handleTotalPriceChange(e.target.value)}
                      />
                      <span className='fw-bolder text-white bg-primary rounded px-4 min-w-70px d-flex align-items-center justify-content-center'>
                        {formik.values.currency}
                      </span>
                    </div>
                    {totalPriceNumber > 0 ? (
                      <div className='form-text fw-semibold text-gray-700'>
                        {formatAmount(totalPriceNumber, formik.values.currency)}
                      </div>
                    ) : null}
                    {renderError('totalPrice')}
                  </div>
                  <div className='col-md-6'>
                    <label className='fw-semibold fs-6 mb-2'>
                      {intl.formatMessage({id: 'MAIL_ORDER.FIELDS.DESCRIPTION'})}
                    </label>
                    <textarea
                      rows={3}
                      className='form-control form-control-solid'
                      {...formik.getFieldProps('description')}
                    />
                  </div>
                  <div className='col-md-6'>
                    <label className='fw-semibold fs-6 mb-2'>
                      {intl.formatMessage({id: 'MAIL_ORDER.FIELDS.SUMMARY'})}
                    </label>
                    <textarea
                      rows={3}
                      className='form-control form-control-solid'
                      {...formik.getFieldProps('circuitSummary')}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* ── Client ─────────────────────────────────────────────── */}
            <div className='card border-0 shadow-sm mb-8'>
              <div className='card-body p-6 p-lg-8'>
                {renderSectionTitle(
                  'MAIL_ORDER.SECTION.CLIENT',
                  'MAIL_ORDER.SECTION.CLIENT_HINT',
                  'bi-person'
                )}
                <div className='row g-5'>
                  <div className='col-md-6'>
                    <label className='required fw-semibold fs-6 mb-2'>
                      {intl.formatMessage({id: 'MAIL_ORDER.FIELDS.CLIENT_NAME'})}
                    </label>
                    <input
                      type='text'
                      className={fieldClass('clientName')}
                      {...formik.getFieldProps('clientName')}
                    />
                    {renderError('clientName')}
                  </div>
                  <div className='col-md-6'>
                    <label className='required fw-semibold fs-6 mb-2'>
                      {intl.formatMessage({id: 'MAIL_ORDER.FIELDS.EMAIL'})}
                    </label>
                    <input
                      type='email'
                      className={fieldClass('clientEmail')}
                      {...formik.getFieldProps('clientEmail')}
                    />
                    {renderError('clientEmail')}
                  </div>
                  <div className='col-md-6'>
                    <label className='required fw-semibold fs-6 mb-2'>
                      {intl.formatMessage({id: 'MAIL_ORDER.FIELDS.PHONE'})}
                    </label>
                    <input
                      type='tel'
                      placeholder='+216 ...'
                      className={fieldClass('clientPhone')}
                      {...formik.getFieldProps('clientPhone')}
                    />
                    {renderError('clientPhone')}
                  </div>
                  <div className='col-md-6'>
                    <label className='fw-semibold fs-6 mb-2'>
                      {intl.formatMessage({id: 'MAIL_ORDER.FIELDS.COUNTRY'})}
                    </label>
                    <input
                      type='text'
                      className='form-control form-control-solid'
                      {...formik.getFieldProps('clientCountry')}
                    />
                  </div>
                  <div className='col-12'>
                    <label className='form-check form-check-custom form-check-solid'>
                      <input
                        className='form-check-input'
                        type='checkbox'
                        checked={formik.values.withVoucher}
                        onChange={(event) =>
                          formik.setFieldValue('withVoucher', event.target.checked)
                        }
                      />
                      <span className='form-check-label fw-semibold text-gray-800'>
                        {intl.formatMessage({id: 'MAIL_ORDER.FIELDS.WITH_VOUCHER'})}
                      </span>
                    </label>
                    <div className='form-text'>
                      {intl.formatMessage({id: 'MAIL_ORDER.FIELDS.WITH_VOUCHER_HINT'})}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* ── Échéances ──────────────────────────────────────────── */}
            <div className='card border-0 shadow-sm mb-8'>
              <div className='card-body p-6 p-lg-8'>
                {renderSectionTitle(
                  'MAIL_ORDER.INSTALLMENTS.TITLE',
                  'MAIL_ORDER.INSTALLMENTS.HINT',
                  'bi-calendar2-check'
                )}
                <p className='text-muted fs-7 mb-5'>
                  {intl.formatMessage({id: 'MAIL_ORDER.INSTALLMENTS.DEFAULT_HINT'})}
                </p>
                <FieldArray name='installments'>
                  {({push, remove}) => (
                    <div>
                      {formik.values.installments.map((_inst, index) => (
                        <div
                          className='border border-gray-300 rounded-3 p-5 mb-4 bg-white'
                          key={index}
                        >
                          <div className='d-flex justify-content-between align-items-center mb-4'>
                            <span className='badge badge-primary fs-7 fw-bolder text-white'>
                              {intl.formatMessage({id: 'MAIL_ORDER.FIELDS.AMOUNT'})} #{index + 1}
                            </span>
                            {formik.values.installments.length > 1 && (
                              <button
                                type='button'
                                className='btn btn-sm btn-light-danger'
                                onClick={() => remove(index)}
                              >
                                {intl.formatMessage({id: 'MAIL_ORDER.INSTALLMENTS.REMOVE'})}
                              </button>
                            )}
                          </div>
                          <div className='row g-4'>
                            <div className='col-md-6'>
                              <label className='required fw-semibold fs-6 mb-2'>
                                {intl.formatMessage({id: 'MAIL_ORDER.FIELDS.AMOUNT'})}
                              </label>
                              <div className='d-flex align-items-stretch gap-3'>
                                <input
                                  type='number'
                                  min={0}
                                  step={formik.values.currency === 'TND' ? '0.001' : '0.01'}
                                  className={clsx('form-control form-control-solid flex-grow-1', {
                                    'is-invalid':
                                      formik.touched.installments?.[index]?.amount &&
                                      (formik.errors.installments?.[index] as any)?.amount,
                                  })}
                                  {...formik.getFieldProps(`installments[${index}].amount`)}
                                />
                                <span className='fw-bolder text-white bg-primary rounded px-4 min-w-70px d-flex align-items-center justify-content-center flex-shrink-0'>
                                  {formik.values.currency}
                                </span>
                              </div>
                            </div>
                            <div className='col-md-6'>
                              <label className='required fw-semibold fs-6 mb-2'>
                                {intl.formatMessage({id: 'MAIL_ORDER.FIELDS.EXPIRES_AT'})}
                              </label>
                              <input
                                type='date'
                                min={todayInputDate()}
                                className={clsx('form-control form-control-solid', {
                                  'is-invalid':
                                    formik.touched.installments?.[index]?.expiresAt &&
                                    (formik.errors.installments?.[index] as any)?.expiresAt,
                                })}
                                {...formik.getFieldProps(`installments[${index}].expiresAt`)}
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                      {totalPriceNumber > 0 && (
                        <div
                          className={clsx('alert py-3 mb-4', {
                            'alert-success': amountsMatch,
                            'alert-warning': userChangedInstallments,
                            'alert-info': !amountsMatch && !userChangedInstallments,
                          })}
                        >
                          {amountsMatch
                            ? intl.formatMessage({id: 'MAIL_ORDER.INSTALLMENTS.BALANCED'})
                            : intl.formatMessage(
                                {
                                  id: userChangedInstallments
                                    ? 'MAIL_ORDER.INSTALLMENTS.AMOUNT_CHANGED'
                                    : 'MAIL_ORDER.INSTALLMENTS.MISMATCH_HINT',
                                },
                                {
                                  sum: formatAmount(installmentsSum, formik.values.currency),
                                  total: formatAmount(totalPriceNumber, formik.values.currency),
                                }
                              )}
                          {!amountsMatch && (
                            <div className='fw-semibold mt-1'>
                              {intl.formatMessage(
                                {id: 'MAIL_ORDER.INSTALLMENTS.REMAINING'},
                                {
                                  amount: formatAmount(
                                    remainingToAllocate,
                                    formik.values.currency
                                  ),
                                }
                              )}
                            </div>
                          )}
                        </div>
                      )}
                      <button
                        type='button'
                        className='btn btn-light-primary btn-sm'
                        onClick={() =>
                          push({
                            amount: remainingToAllocate > 0 ? remainingToAllocate : '',
                            expiresAt: defaultInstallmentExpiry(),
                          })
                        }
                      >
                        <i className='bi bi-plus-lg me-1' />
                        {intl.formatMessage({id: 'MAIL_ORDER.INSTALLMENTS.ADD'})}
                      </button>
                    </div>
                  )}
                </FieldArray>
              </div>
            </div>
          </fieldset>

          <div className='d-flex flex-wrap justify-content-end gap-3 pb-4'>
            <button
              type='button'
              className='btn btn-light-primary fw-semibold px-6'
              onClick={() => navigate('/apps/mail-orders')}
              disabled={formik.isSubmitting}
            >
              {intl.formatMessage({id: 'MAIL_ORDER.BACK_TO_LIST'})}
            </button>
            {!readOnly && (
              <button
                type='submit'
                className='btn btn-primary fw-semibold px-8'
                disabled={formik.isSubmitting || loading}
              >
                <span className='indicator-label'>
                  {intl.formatMessage({id: 'HEADER.SAVE'})}
                </span>
                {formik.isSubmitting && (
                  <span className='indicator-progress'>
                    {intl.formatMessage({id: 'COMMON.PLEASE_WAIT'})}{' '}
                    <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                  </span>
                )}
              </button>
            )}
          </div>
        </form>
      </FormikProvider>
      {loading && <DataLoading />}
    </>
  )
}

export default MailOrderFormPage
